import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import AuthProvider, { useAuth } from './context/AuthContext';
import CartProvider from './context/CartContext';

// Common Components
import Header from './components/Header';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';

// Page Components (will create these later)
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoriesPage from './pages/CategoriesPage';
import CategoryPage from './pages/CategoryPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import BulkOrderPage from './pages/BulkOrderPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import Products from './pages/admin/Products';
import Orders from './pages/admin/Orders';
import Customers from './pages/admin/Customers';
import Settings from './pages/admin/Settings';
import BulkOrdersAdmin from './pages/admin/BulkOrdersAdmin';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <div>Loading...</div>;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) return <div>Loading...</div>;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <Routes>
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="orders" element={<Orders />} />
              <Route path="customers" element={<Customers />} />
              <Route path="settings" element={<Settings />} />
              <Route path="bulk-requests" element={<BulkOrdersAdmin />} />
            </Route>

            {/* Frontend Routes */}
            <Route path="/" element={
              <>
                <Header />
                <main className="main-content">
                  <HomePage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/products" element={
              <>
                <Header />
                <main className="main-content">
                  <ProductsPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/products/:id" element={
              <>
                <Header />
                <main className="main-content">
                  <ProductDetailPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/categories" element={
              <>
                <Header />
                <main className="main-content">
                  <CategoriesPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/categories/:id" element={
              <>
                <Header />
                <main className="main-content">
                  <CategoryPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/cart" element={
              <>
                <Header />
                <main className="main-content">
                  <CartPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/about" element={
              <>
                <Header />
                <main className="main-content">
                  <AboutPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/contact" element={
              <>
                <Header />
                <main className="main-content">
                  <ContactPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/bulk-orders" element={
              <>
                <Header />
                <main className="main-content">
                  <BulkOrderPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/login" element={
              <>
                <Header />
                <main className="main-content">
                  <LoginPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/register" element={
              <>
                <Header />
                <main className="main-content">
                  <RegisterPage />
                </main>
                <Footer />
              </>
            } />

            {/* Protected Routes */}
            <Route path="/checkout" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <main className="main-content">
                    <CheckoutPage />
                  </main>
                  <Footer />
                </>
              </ProtectedRoute>
            } />

            <Route path="/order-success/:id" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <main className="main-content">
                    <OrderSuccessPage />
                  </main>
                  <Footer />
                </>
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <main className="main-content">
                    <ProfilePage />
                  </main>
                  <Footer />
                </>
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <main className="main-content">
                    <OrdersPage />
                  </main>
                  <Footer />
                </>
              </ProtectedRoute>
            } />

            <Route path="/orders/:id" element={
              <ProtectedRoute>
                <>
                  <Header />
                  <main className="main-content">
                    <OrderDetailPage />
                  </main>
                  <Footer />
                </>
              </ProtectedRoute>
            } />

            <Route path="/order-confirmation/:orderId" element={
              <>
                <Header />
                <main className="main-content">
                  <OrderConfirmationPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/privacy-policy" element={
              <>
                <Header />
                <main className="main-content">
                  <PrivacyPolicyPage />
                </main>
                <Footer />
              </>
            } />

            {/* 404 Route */}
            <Route path="*" element={
              <>
                <Header />
                <main className="main-content">
                  <NotFoundPage />
                </main>
                <Footer />
              </>
            } />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
